/**
 * Lounge Access Token Utilities
 * Handles token storage, validation, and expiration
 */

const LOUNGE_TOKEN_KEY = 'loungeAccessToken';
const LOUNGE_TOKEN_EXPIRY_KEY = 'loungeAccessTokenExpiry';
const TOKEN_LIFETIME_MS = 15 * 60 * 1000; // 15 minutes
// For testing purposes, set token lifetime to 1 minutes
// const TOKEN_LIFETIME_MS = 1 * 60 * 1000; // 1 minute

export interface LoungeTokenInfo {
  token: string;
  expiresAt: number;
  remainingMs: number;
}

/**
 * Get the lounge access token if valid
 */
export const getLoungeToken = (): string | null => {
  const token = localStorage.getItem(LOUNGE_TOKEN_KEY);
  const expiry = localStorage.getItem(LOUNGE_TOKEN_EXPIRY_KEY);

  if (!token || !expiry) {
    return null;
  }

  const expiryTime = parseInt(expiry, 10);
  if (Date.now() >= expiryTime) {
    // Token expired, clean up
    clearLoungeToken();
    return null;
  }

  return token;
};

/**
 * Get detailed token info including expiration
 */
export const getLoungeTokenInfo = (): LoungeTokenInfo | null => {
  const token = localStorage.getItem(LOUNGE_TOKEN_KEY);
  const expiry = localStorage.getItem(LOUNGE_TOKEN_EXPIRY_KEY);

  if (!token || !expiry) {
    return null;
  }

  const expiresAt = parseInt(expiry, 10);
  const remainingMs = expiresAt - Date.now();

  if (remainingMs <= 0) {
    clearLoungeToken();
    return null;
  }

  return {
    token,
    expiresAt,
    remainingMs,
  };
};

/**
 * Check if lounge token is valid
 */
export const isLoungeTokenValid = (): boolean => {
  return getLoungeToken() !== null;
};

/**
 * Set the lounge access token with expiration
 */
export const setLoungeToken = (token: string): void => {
  const expirationTime = Date.now() + TOKEN_LIFETIME_MS;
  localStorage.setItem(LOUNGE_TOKEN_KEY, token);
  localStorage.setItem(LOUNGE_TOKEN_EXPIRY_KEY, expirationTime.toString());
};

/**
 * Clear the lounge access token
 */
export const clearLoungeToken = (): void => {
  localStorage.removeItem(LOUNGE_TOKEN_KEY);
  localStorage.removeItem(LOUNGE_TOKEN_EXPIRY_KEY);
};

/**
 * Get remaining time in milliseconds
 */
export const getRemainingTime = (): number => {
  const info = getLoungeTokenInfo();
  return info?.remainingMs ?? 0;
};
